/**
 * Reference-link capture. A partner org is handed a link like
 * `https://deletedoordash.com/?ref=their-name`; this remembers the slug so a
 * signup made later can be attributed back to whoever sent the visitor.
 *
 * Why localStorage and not just the URL at submit time: people arrive through an
 * org's link, read the page, close the tab, and come back directly a day later
 * to sign. Reading the live query string would credit that signup to nobody.
 * Persisting the slug keeps the attribution the org actually earned.
 *
 * Why a query param and not a path: the CloudFront cache behaviour has
 * QueryString=false, so `?ref=` serves the same cached homepage (no cache
 * fragmentation, no new routes) while CloudFront still records the raw query in
 * its access logs -- which is what gives the traffic Lambda per-ref page views.
 *
 * Nothing here is PII: the slug is a string WE minted and handed to an org, not
 * anything about the visitor. It is never displayed to the visitor and never
 * appears in any public API response.
 */

import { normalizeRef } from '../shared/types';

/** localStorage key holding the last valid ref this browser arrived through. */
export const REF_STORAGE_KEY = 'ddd_ref';

/**
 * `window.localStorage` when it is usable, else null.
 *
 * Reading the PROPERTY (not just calling it) throws in Safari private mode and
 * whenever storage is blocked by policy, so this is a real failure mode, not a
 * hypothetical. Callers take `Storage | null` and simply do nothing without it:
 * losing attribution is acceptable, breaking the page over a tracking tag is not.
 */
export function safeLocalStorage(): Storage | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

/**
 * Capture `?ref=<slug>` from a query string into storage.
 *
 * LAST TOUCH WINS: a newer valid ref overwrites a stored one, because the link
 * someone most recently clicked is the one that brought them back. An absent or
 * malformed ref leaves whatever is stored alone -- navigating around the site
 * (every other link has no `?ref=`) must not erase the attribution.
 *
 * Pure but for the injected `storage`, so it tests without a real browser.
 */
export function captureRef(search: string, storage: Storage | null): void {
  if (!storage) return;
  let raw: string | null;
  try {
    raw = new URLSearchParams(search).get('ref');
  } catch {
    return;
  }
  if (raw === null) return;

  const slug = normalizeRef(raw);
  if (!slug) return;

  try {
    storage.setItem(REF_STORAGE_KEY, slug);
  } catch {
    // Quota or a locked-down storage: attribution is best effort, never fatal.
  }
}

/**
 * The stored ref, re-validated on the way out. Storage is user-writable, so a
 * value read back from it is exactly as untrusted as the query string was --
 * anything that is not a canonical slug is treated as absent rather than sent
 * to the API for the server to reject.
 */
export function storedRef(storage: Storage | null): string | undefined {
  if (!storage) return undefined;
  let raw: string | null;
  try {
    raw = storage.getItem(REF_STORAGE_KEY);
  } catch {
    return undefined;
  }
  if (raw === null) return undefined;
  return normalizeRef(raw) ?? undefined;
}
